import { Signal, signal } from '@angular/core';
import { Params } from '@angular/router';
import { StorageKey, StorageService } from '@lib/core';
import { PurchasingGroup } from '../../models/purchasing-group.model';

export enum PurchasingGroupStatusFilter {
    ALL = '',
    ACTIVE = 'active',
    INACTIVE = 'inactive',
}

export interface IPurchasingGroupListFilterDto {
    readonly code: string;
    readonly name: string;
    readonly companyCodes: ReadonlyArray<string>;
    readonly materialGroupCodes: ReadonlyArray<string>;
    readonly memberIds: ReadonlyArray<string>;
    readonly status: PurchasingGroupStatusFilter;
}

enum PurchasingGroupListFilterKey {
    CODE = 'code',
    NAME = 'name',
    COMPANY_CODES = 'companyCodes',
    MATERIAL_GROUP_CODES = 'materialGroupCodes',
    MEMBER_IDS = 'memberIds',
    STATUS = 'status',
}

const EMPTY_DTO: IPurchasingGroupListFilterDto = {
    code: '',
    name: '',
    companyCodes: [],
    materialGroupCodes: [],
    memberIds: [],
    status: PurchasingGroupStatusFilter.ALL,
};

export class PurchasingGroupListFilter {
    public static readonly CLEAN_FILTER_KEY = 'clean';

    public static async restore(
        storage: StorageService,
        params: Params,
    ): Promise<PurchasingGroupListFilter> {
        const filter = new PurchasingGroupListFilter(storage);

        if (params[PurchasingGroupListFilter.CLEAN_FILTER_KEY]) {
            await storage.removeItem(StorageKey.PURCHASING_GROUP_LIST_FILTER);
            return filter;
        }

        if (PurchasingGroupListFilter.hasRouteParams(params)) {
            return PurchasingGroupListFilter.restoreFromRouteParams(filter, params);
        }

        return PurchasingGroupListFilter.restoreFromStorage(filter, storage);
    }

    private static hasRouteParams(params: Params): boolean {
        return Boolean(
            params[PurchasingGroupListFilterKey.CODE] ||
                params[PurchasingGroupListFilterKey.NAME] ||
                params[PurchasingGroupListFilterKey.COMPANY_CODES] ||
                params[PurchasingGroupListFilterKey.MATERIAL_GROUP_CODES] ||
                params[PurchasingGroupListFilterKey.MEMBER_IDS] ||
                params[PurchasingGroupListFilterKey.STATUS],
        );
    }

    private static async restoreFromRouteParams(
        filter: PurchasingGroupListFilter,
        params: Params,
    ): Promise<PurchasingGroupListFilter> {
        await filter.update({
            code: (params[PurchasingGroupListFilterKey.CODE] as string) ?? '',
            name: (params[PurchasingGroupListFilterKey.NAME] as string) ?? '',
            companyCodes: PurchasingGroupListFilter.toStringArray(
                params[PurchasingGroupListFilterKey.COMPANY_CODES],
            ),
            materialGroupCodes: PurchasingGroupListFilter.toStringArray(
                params[PurchasingGroupListFilterKey.MATERIAL_GROUP_CODES],
            ),
            memberIds: PurchasingGroupListFilter.toStringArray(
                params[PurchasingGroupListFilterKey.MEMBER_IDS],
            ),
            status: PurchasingGroupListFilter.toStatus(params[PurchasingGroupListFilterKey.STATUS]),
        });
        return filter;
    }

    private static async restoreFromStorage(
        filter: PurchasingGroupListFilter,
        storage: StorageService,
    ): Promise<PurchasingGroupListFilter> {
        if (await storage.has(StorageKey.PURCHASING_GROUP_LIST_FILTER)) {
            const dto = await storage.getItemObject<IPurchasingGroupListFilterDto>(
                StorageKey.PURCHASING_GROUP_LIST_FILTER,
            );
            filter._dto.set({
                code: dto.code ?? '',
                name: dto.name ?? '',
                companyCodes: dto.companyCodes ? [...dto.companyCodes] : [],
                materialGroupCodes: dto.materialGroupCodes ? [...dto.materialGroupCodes] : [],
                memberIds: dto.memberIds ? [...dto.memberIds] : [],
                status: PurchasingGroupListFilter.toStatus(dto.status),
            });
        }
        return filter;
    }

    private static toStringArray(value: unknown): string[] {
        if (Array.isArray(value)) {
            return value.map((entry) => String(entry)).filter((entry) => entry.length > 0);
        }
        if (typeof value === 'string' && value.length > 0) {
            return [value];
        }
        return [];
    }

    private static toStatus(value: unknown): PurchasingGroupStatusFilter {
        if (value === PurchasingGroupStatusFilter.ACTIVE || value === PurchasingGroupStatusFilter.INACTIVE) {
            return value;
        }
        return PurchasingGroupStatusFilter.ALL;
    }

    private readonly _dto = signal<IPurchasingGroupListFilterDto>(EMPTY_DTO);

    public readonly dto: Signal<IPurchasingGroupListFilterDto> = this._dto.asReadonly();

    public get activeCount(): number {
        const dto = this._dto();
        let count = 0;
        if (dto.code) count++;
        if (dto.name) count++;
        if (dto.companyCodes.length) count++;
        if (dto.materialGroupCodes.length) count++;
        if (dto.memberIds.length) count++;
        if (dto.status) count++;
        return count;
    }

    public get isEmpty(): boolean {
        return this.activeCount === 0;
    }

    public get asRouteParams(): Params {
        const dto = this._dto();
        const params: Params = {};
        if (dto.code) params[PurchasingGroupListFilterKey.CODE] = dto.code;
        if (dto.name) params[PurchasingGroupListFilterKey.NAME] = dto.name;
        if (dto.companyCodes.length) {
            params[PurchasingGroupListFilterKey.COMPANY_CODES] = [...dto.companyCodes];
        }
        if (dto.materialGroupCodes.length) {
            params[PurchasingGroupListFilterKey.MATERIAL_GROUP_CODES] = [...dto.materialGroupCodes];
        }
        if (dto.memberIds.length) {
            params[PurchasingGroupListFilterKey.MEMBER_IDS] = [...dto.memberIds];
        }
        if (dto.status) params[PurchasingGroupListFilterKey.STATUS] = dto.status;
        return params;
    }

    public get clearedRouteParams(): Params {
        return {
            [PurchasingGroupListFilterKey.CODE]: null,
            [PurchasingGroupListFilterKey.NAME]: null,
            [PurchasingGroupListFilterKey.COMPANY_CODES]: null,
            [PurchasingGroupListFilterKey.MATERIAL_GROUP_CODES]: null,
            [PurchasingGroupListFilterKey.MEMBER_IDS]: null,
            [PurchasingGroupListFilterKey.STATUS]: null,
        };
    }

    public constructor(private readonly _storage: StorageService) {}

    public async update(values: Partial<IPurchasingGroupListFilterDto>): Promise<void> {
        this._dto.update((current) => ({
            code: values.code !== undefined ? values.code.trim() : current.code,
            name: values.name !== undefined ? values.name.trim() : current.name,
            companyCodes:
                values.companyCodes !== undefined ? [...values.companyCodes] : current.companyCodes,
            materialGroupCodes:
                values.materialGroupCodes !== undefined
                    ? [...values.materialGroupCodes]
                    : current.materialGroupCodes,
            memberIds: values.memberIds !== undefined ? [...values.memberIds] : current.memberIds,
            status:
                values.status !== undefined
                    ? PurchasingGroupListFilter.toStatus(values.status)
                    : current.status,
        }));
        await this.save();
    }

    public async setStatus(status: PurchasingGroupStatusFilter): Promise<void> {
        await this.update({ status });
    }

    public async clear(): Promise<void> {
        this._dto.set(EMPTY_DTO);
        await this.save();
    }

    public async clearKey(key: keyof IPurchasingGroupListFilterDto): Promise<void> {
        switch (key) {
            case 'code':
                await this.update({ code: '' });
                break;
            case 'name':
                await this.update({ name: '' });
                break;
            case 'companyCodes':
                await this.update({ companyCodes: [] });
                break;
            case 'materialGroupCodes':
                await this.update({ materialGroupCodes: [] });
                break;
            case 'memberIds':
                await this.update({ memberIds: [] });
                break;
            case 'status':
                await this.update({ status: PurchasingGroupStatusFilter.ALL });
                break;
        }
    }

    public matches(purchasingGroup: PurchasingGroup): boolean {
        const dto = this._dto();
        if (dto.code && !purchasingGroup.code.toLowerCase().includes(dto.code.toLowerCase())) {
            return false;
        }
        if (dto.name && !purchasingGroup.name.toLowerCase().includes(dto.name.toLowerCase())) {
            return false;
        }
        if (dto.companyCodes.length) {
            const hit = purchasingGroup.companyCodes.some((code) => dto.companyCodes.includes(code));
            if (!hit) return false;
        }
        if (dto.materialGroupCodes.length) {
            const hit = purchasingGroup.materialGroupCodes.some((code) =>
                dto.materialGroupCodes.includes(code),
            );
            if (!hit) return false;
        }
        if (dto.memberIds.length) {
            const hit = purchasingGroup.memberIds.some((id) => dto.memberIds.includes(id));
            if (!hit) return false;
        }
        if (dto.status === PurchasingGroupStatusFilter.ACTIVE && !purchasingGroup.active) return false;
        if (dto.status === PurchasingGroupStatusFilter.INACTIVE && purchasingGroup.active) return false;
        return true;
    }

    public toDto(): IPurchasingGroupListFilterDto {
        const dto = this._dto();
        return {
            code: dto.code,
            name: dto.name,
            companyCodes: [...dto.companyCodes],
            materialGroupCodes: [...dto.materialGroupCodes],
            memberIds: [...dto.memberIds],
            status: dto.status,
        };
    }

    private async save(): Promise<void> {
        await this._storage.setItem(
            StorageKey.PURCHASING_GROUP_LIST_FILTER,
            JSON.stringify(this.toDto()),
        );
    }
}
